/**
 * @file pages/Search.jsx
 * @description Professional OTA search page with Flights, Buses, and Trains.
 * Each tab redirects to an external partner after search:
 *   - Flights → Skyscanner
 *   - Buses → RedBus
 *   - Trains → IRCTC
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Bus, TrainFront, Search as SearchIcon, ArrowRight, Clock, Users,
  MapPin, ArrowLeftRight, ChevronRight,
  ExternalLink, Shield, Percent, Gift, Sparkles, CheckCircle2
} from 'lucide-react';
import Header from '../Components/Layouts/Header';
import Footer from '../Components/Layouts/Footer';
import LocationAutocomplete from '../Components/Transport/LocationAutocomplete';
import AnimatedButton from '../Components/Ui/AnimatedButton';

// ─── TABS CONFIG ──────────────────────────────────────────────────
const TABS = [
  { id: 'flights', label: 'Flights', icon: Plane, desc: 'Domestic & International', partner: 'Skyscanner', partnerColor: 'from-sky-500 to-blue-600', partnerLogo: '✈️' },
  { id: 'buses', label: 'Buses', icon: Bus, desc: 'AC, Sleeper & Volvo', partner: 'RedBus', partnerColor: 'from-red-500 to-red-600', partnerLogo: '🚌' },
  { id: 'trains', label: 'Trains', icon: TrainFront, desc: 'All Indian Railways', partner: 'IRCTC', partnerColor: 'from-orange-500 to-amber-600', partnerLogo: '🚆' },
];

const SPECIAL_FARES = [
  { id: 'regular', label: 'Regular', desc: 'Regular fares', active: true },
  { id: 'student', label: 'Student', desc: 'Extra discounts/baggage' },
  { id: 'senior', label: 'Senior Citizen', desc: 'Up to ₹600 off' },
  { id: 'armed', label: 'Armed Forces', desc: 'Up to ₹600 off' },
];

const OFFERS = [
  { title: 'Flat 15% OFF', subtitle: 'on domestic flights', code: 'NIKFLY15', bg: 'from-blue-600 to-indigo-700', icon: Plane },
  { title: 'Up to ₹500 OFF', subtitle: 'on bus bookings', code: 'NIKBUS500', bg: 'from-orange-500 to-red-500', icon: Bus },
  { title: 'Train Tatkal Tips', subtitle: 'book faster with us', code: 'NIKTRAIN', bg: 'from-amber-500 to-orange-600', icon: TrainFront },
  { title: 'Group Discount', subtitle: '4+ travellers get 20% off', code: 'NIKGROUP', bg: 'from-violet-500 to-purple-600', icon: Users },
];

const COLLECTIONS = [
  { title: 'Weekend in Goa', img: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80', tag: 'TOP 5', from: '₹2,499' },
  { title: 'Royal Rajasthan', img: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=400&q=80', tag: 'TOP 8', from: '₹1,899' },
  { title: 'Kerala Backwaters', img: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=80', tag: 'TOP 6', from: '₹3,299' },
  { title: 'Himalayan Escape', img: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=400&q=80', tag: 'TOP 4', from: '₹4,599' },
  { title: 'Temple Circuit', img: 'https://images.unsplash.com/photo-1548013146-72479768bada?w=400&q=80', tag: 'TOP 7', from: '₹1,499' },
];

// ─── CITY TO IATA (for Skyscanner deep link) ─────────────────────
const CITY_IATA = {
  'delhi': 'DEL', 'new delhi': 'DEL', 'mumbai': 'BOM', 'bombay': 'BOM',
  'bangalore': 'BLR', 'bengaluru': 'BLR', 'chennai': 'MAA', 'kolkata': 'CCU',
  'hyderabad': 'HYD', 'pune': 'PNQ', 'ahmedabad': 'AMD', 'goa': 'GOI',
  'jaipur': 'JAI', 'lucknow': 'LKO', 'kochi': 'COK', 'cochin': 'COK',
  'thiruvananthapuram': 'TRV', 'trivandrum': 'TRV', 'guwahati': 'GAU',
  'patna': 'PAT', 'chandigarh': 'IXC', 'indore': 'IDR', 'bhopal': 'BHO',
  'nagpur': 'NAG', 'varanasi': 'VNS', 'amritsar': 'ATQ', 'srinagar': 'SXR',
  'leh': 'IXL', 'dehradun': 'DED', 'ranchi': 'IXR', 'bhubaneswar': 'BBI',
  'visakhapatnam': 'VTZ', 'vizag': 'VTZ', 'coimbatore': 'CJB', 'udaipur': 'UDR',
  'jodhpur': 'JDH', 'raipur': 'RPR', 'bagdogra': 'IXB', 'siliguri': 'IXB',
  'dubai': 'DXB', 'singapore': 'SIN', 'bangkok': 'BKK', 'london': 'LHR',
  'new york': 'JFK', 'paris': 'CDG', 'toronto': 'YYZ', 'tokyo': 'NRT',
  'sydney': 'SYD', 'doha': 'DOH', 'colombo': 'CMB', 'kathmandu': 'KTM',
};

function resolveIATA(input) {
  if (!input) return '';
  const trimmed = input.trim();
  // Already a 3-letter code
  if (/^[A-Z]{3}$/i.test(trimmed)) return trimmed.toUpperCase();
  // Extract from "City (CODE)" format
  const match = trimmed.match(/\(([A-Z]{3})\)/);
  if (match) return match[1];
  // Lookup map
  return CITY_IATA[trimmed.toLowerCase()] || trimmed.toUpperCase().slice(0, 3);
}

function extractCityName(input) {
  if (!input) return '';
  // Remove IATA code from "City (CODE)" format
  return input.replace(/\s*\([A-Z]{3}\)\s*/g, '').trim();
}

// ─── EXTERNAL URL BUILDERS ───────────────────────────────────────
function buildSkyscannerUrl(origin, destination, date) {
  const from = resolveIATA(origin);
  const to = resolveIATA(destination);
  // Skyscanner date format: YYMMDD
  const d = new Date(date);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `https://www.skyscanner.co.in/transport/flights/${from}/${to}/${yy}${mm}${dd}/`;
}

function buildRedBusUrl(origin, destination, date) {
  const from = extractCityName(origin).toLowerCase().replace(/\s+/g, '-');
  const to = extractCityName(destination).toLowerCase().replace(/\s+/g, '-');
  // RedBus date format: DD-Mon-YYYY
  const d = new Date(date);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateStr = `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
  return `https://www.redbus.in/bus-tickets/${from}-to-${to}?date=${dateStr}`;
}

function buildIRCTCUrl() {
  return 'https://www.irctc.co.in/nget/train-search';
}

// ─── SEARCH FORM ──────────────────────────────────────────────────
const SearchForm = ({ activeTab, onSearch, redirecting }) => {
  const [origin, setOrigin] = useState('');
  const [originData, setOriginData] = useState(null);
  const [destination, setDestination] = useState('');
  const [destData, setDestData] = useState(null);
  const [date, setDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [tripType, setTripType] = useState('one_way');
  const [selectedFare, setSelectedFare] = useState('regular');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!origin.trim() || !destination.trim() || !date) return;
    onSearch({ origin, destination, date, passengers, tripType });
  };

  const swapCities = () => {
    setOrigin(destination); setDestination(origin);
    setOriginData(destData); setDestData(originData);
  };
  const today = new Date().toISOString().split('T')[0];
  const mode = activeTab === 'flights' ? 'flight' : 'bus';

  const tabInfo = TABS.find(t => t.id === activeTab);

  return (
    <form onSubmit={handleSubmit}>
      {/* Partner Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {activeTab === 'flights' && (
            <div className="flex gap-6">
              {[{ v: 'one_way', l: 'One Way' }, { v: 'round_trip', l: 'Round Trip' }].map(t => (
                <label key={t.v} className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                    tripType === t.v ? 'border-[#243185]' : 'border-gray-300 group-hover:border-gray-400'
                  }`}>
                    {tripType === t.v && <div className="w-2 h-2 rounded-full bg-[#243185]" />}
                  </div>
                  <input type="radio" value={t.v} checked={tripType === t.v} onChange={() => setTripType(t.v)} className="sr-only" />
                  <span className={`text-sm font-medium ${tripType === t.v ? 'text-gray-900' : 'text-gray-500'}`}>{t.l}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${tabInfo?.partnerColor} text-white text-xs font-bold shadow-sm`}>
          <ExternalLink size={12} />
          Powered by {tabInfo?.partner}
        </div>
      </div>

      {/* City + Date Row */}
      <div className="flex flex-col md:flex-row border border-gray-200 rounded-xl bg-white divide-y md:divide-y-0 md:divide-x divide-gray-200 overflow-visible">
        {/* FROM */}
        <div className="flex-1 p-4 hover:bg-blue-50/30 transition-colors cursor-text group relative">
          <LocationAutocomplete
            value={origin}
            onChange={setOrigin}
            onSelect={(item) => { setOriginData(item); if (item) setOrigin(item.displayName); }}
            label="From"
            placeholder={activeTab === 'trains' ? 'Enter city or station' : 'Enter city or airport'}
            mode={mode}
          />
        </div>

        {/* SWAP */}
        <div className="hidden md:flex items-center justify-center relative" style={{ margin: '-1px' }}>
          <button type="button" onClick={swapCities}
            className="absolute z-20 w-9 h-9 rounded-full bg-white border-2 border-[#243185] hover:bg-[#243185] hover:text-white text-[#243185] flex items-center justify-center transition-all shadow-md hover:shadow-lg">
            <ArrowLeftRight size={15} />
          </button>
        </div>

        {/* TO */}
        <div className="flex-1 p-4 hover:bg-blue-50/30 transition-colors cursor-text">
          <LocationAutocomplete
            value={destination}
            onChange={setDestination}
            onSelect={(item) => { setDestData(item); if (item) setDestination(item.displayName); }}
            label="To"
            placeholder={activeTab === 'trains' ? 'Enter city or station' : 'Enter city or airport'}
            mode={mode}
          />
        </div>

        {/* DATE */}
        <div className="md:w-48 p-4 hover:bg-blue-50/30 transition-colors cursor-pointer"
          onClick={() => document.getElementById('search-date')?.showPicker?.()}>
          <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">Departure</label>
          <input id="search-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} min={today}
            className="w-full text-lg font-bold text-gray-900 bg-transparent outline-none mt-1 cursor-pointer" required />
          {!date && <p className="text-xs text-gray-400 mt-0.5">Select date</p>}
        </div>

        {/* TRAVELLERS */}
        <div className="md:w-52 p-4 hover:bg-blue-50/30 transition-colors">
          <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            {activeTab === 'trains' ? 'Travellers & Class' : 'Travellers & Class'}
          </label>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-xl font-bold text-gray-900">{passengers}</span>
            <span className="text-sm text-gray-500">Traveller{passengers > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {[1,2,3,4,5,6].map(n => (
              <button key={n} type="button" onClick={() => setPassengers(n)}
                className={`w-7 h-7 rounded-full text-xs font-bold transition-all ${
                  passengers === n ? 'bg-[#243185] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}>{n}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Special Fares */}
      {activeTab === 'flights' && (
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Special Fares</span>
          <div className="flex gap-2 flex-wrap">
            {SPECIAL_FARES.map(f => (
              <button key={f.id} type="button" onClick={() => setSelectedFare(f.id)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all ${
                  selectedFare === f.id
                    ? 'border-[#243185] bg-[#243185]/5 text-[#243185]'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}>
                <span className="block">{f.label}</span>
                <span className="block text-[10px] font-normal opacity-70">{f.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SEARCH BUTTON */}
      <div className="flex justify-center mt-6">
        <AnimatedButton type="submit" loading={redirecting} disabled={redirecting} variant="gradient" size="xl" icon={ExternalLink} iconPosition="right">
          Search on {tabInfo?.partner}
        </AnimatedButton>
      </div>
    </form>
  );
};

// ─── REDIRECT CONFIRMATION ───────────────────────────────────────
const RedirectCard = ({ searchData, activeTab, onReset }) => {
  const tabInfo = TABS.find(t => t.id === activeTab);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-8 md:p-10 text-center max-w-lg mx-auto"
    >
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tabInfo?.partnerColor} flex items-center justify-center mx-auto mb-5 text-2xl`}>
        {tabInfo?.partnerLogo}
      </div>
      <div className="flex items-center justify-center gap-2 mb-3">
        <CheckCircle2 size={20} className="text-emerald-500" />
        <span className="text-sm font-bold text-emerald-600">Redirected Successfully</span>
      </div>
      <h3 className="text-xl font-black text-gray-900 mb-2">
        Searching on {tabInfo?.partner}
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        We've opened {tabInfo?.partner} in a new tab with your search for
      </p>
      <div className="inline-flex items-center gap-3 bg-gray-50 rounded-xl px-6 py-3 mb-6">
        <div className="text-right">
          <p className="text-sm font-bold text-gray-900">{extractCityName(searchData.origin)}</p>
        </div>
        <ArrowRight size={16} className="text-[#243185]" />
        <div className="text-left">
          <p className="text-sm font-bold text-gray-900">{extractCityName(searchData.destination)}</p>
        </div>
        <div className="w-px h-6 bg-gray-200" />
        <p className="text-sm font-semibold text-gray-600">
          {new Date(searchData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <AnimatedButton variant="outline" size="md" onClick={onReset}>
          New Search
        </AnimatedButton>
        <AnimatedButton variant="gradient" size="md" icon={ExternalLink} iconPosition="right"
          onClick={() => {
            let url;
            if (activeTab === 'flights') url = buildSkyscannerUrl(searchData.origin, searchData.destination, searchData.date);
            else if (activeTab === 'buses') url = buildRedBusUrl(searchData.origin, searchData.destination, searchData.date);
            else url = buildIRCTCUrl();
            window.open(url, '_blank');
          }}
        >
          Open {tabInfo?.partner} Again
        </AnimatedButton>
      </div>

      {activeTab === 'trains' && (
        <p className="text-xs text-gray-400 mt-4">
          💡 Tip: Search for "{extractCityName(searchData.origin)} to {extractCityName(searchData.destination)}" on IRCTC after the page loads.
        </p>
      )}
    </motion.div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────
const Search = () => {
  const [urlParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(urlParams.get('tab') || 'flights');
  const [redirecting, setRedirecting] = useState(false);
  const [searchData, setSearchData] = useState(null);

  // Sync tab from URL query param
  useEffect(() => {
    const tab = urlParams.get('tab');
    if (tab && ['flights', 'buses', 'trains'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [urlParams]);

  const handleSearch = async (params) => {
    setRedirecting(true);

    // Build external URL
    let url;
    if (activeTab === 'flights') {
      url = buildSkyscannerUrl(params.origin, params.destination, params.date);
    } else if (activeTab === 'buses') {
      url = buildRedBusUrl(params.origin, params.destination, params.date);
    } else {
      url = buildIRCTCUrl();
    }

    // Brief delay for animation feedback
    await new Promise(r => setTimeout(r, 800));

    // Open external site
    window.open(url, '_blank');

    setRedirecting(false);
    setSearchData(params);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchData(null);
  };

  const handleReset = () => setSearchData(null);

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <Helmet>
        <title>Search Flights, Buses & Trains | Nikaba Travels</title>
        <meta name="description" content="Search and compare flights on Skyscanner, buses on RedBus, and trains on IRCTC — all from one place. Powered by Nikaba Travels." />
      </Helmet>
      <Header />

      {/* ════ HERO + SEARCH ════ */}
      <div className="relative">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2463] via-[#243185] to-[#2d3d9e] h-[440px]" />
        <div className="absolute inset-0 h-[440px] opacity-10"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

        <div className="relative container mx-auto px-4 pt-28 pb-6">
          {/* ─── Icon Tab Bar ─── */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 inline-flex gap-1 mb-8">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                  className={`flex flex-col items-center px-6 md:px-8 py-3 rounded-xl transition-all duration-200 min-w-[90px] ${
                    isActive
                      ? 'bg-white text-[#243185] shadow-lg shadow-black/10'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}>
                  <Icon size={24} strokeWidth={isActive ? 2.2 : 1.8} />
                  <span className="text-sm font-bold mt-1.5">{tab.label}</span>
                  <span className={`text-[10px] mt-0.5 ${isActive ? 'text-gray-500' : 'text-white/50'}`}>{tab.desc}</span>
                </button>
              );
            })}
          </div>

          {/* ─── Search Widget ─── */}
          <div className="bg-white rounded-2xl shadow-2xl shadow-black/15 p-6 md:p-8">
            <SearchForm activeTab={activeTab} onSearch={handleSearch} redirecting={redirecting} />
          </div>
        </div>
      </div>

      {/* ════ REDIRECT CONFIRMATION OR LANDING ════ */}
      <div className="container mx-auto px-4 py-8">

        {/* Redirect Confirmation */}
        <AnimatePresence>
          {searchData && (
            <RedirectCard searchData={searchData} activeTab={activeTab} onReset={handleReset} />
          )}
        </AnimatePresence>

        {/* ════ LANDING CONTENT (no search yet) ════ */}
        {!searchData && (
          <div className="space-y-10 -mt-2">

            {/* Partner Badges */}
            <section className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 text-center">Our Booking Partners</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {TABS.map(tab => (
                  <div key={tab.id} className={`bg-gradient-to-r ${tab.partnerColor} rounded-xl p-5 text-white flex items-center gap-4`}>
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                      {tab.partnerLogo}
                    </div>
                    <div>
                      <p className="font-black">{tab.partner}</p>
                      <p className="text-xs text-white/80">{tab.label} — {tab.desc}</p>
                    </div>
                    <ExternalLink size={16} className="text-white/50 ml-auto flex-shrink-0" />
                  </div>
                ))}
              </div>
            </section>

            {/* Offers Grid */}
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900">Offers</h2>
                <button className="text-sm text-[#243185] font-semibold hover:underline flex items-center gap-1">
                  View All <ChevronRight size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {OFFERS.map((offer, i) => {
                  const Icon = offer.icon;
                  return (
                    <div key={i} className={`bg-gradient-to-br ${offer.bg} rounded-xl p-5 text-white relative overflow-hidden group cursor-pointer hover:shadow-lg transition-all`}>
                      <div className="absolute top-0 right-0 opacity-10 transform translate-x-4 -translate-y-4">
                        <Icon size={80} />
                      </div>
                      <p className="text-xs font-medium opacity-80">T&C'S APPLY</p>
                      <p className="text-lg font-black mt-1">{offer.title}</p>
                      <p className="text-sm opacity-90 mt-1">{offer.subtitle}</p>
                      <div className="flex items-center gap-2 mt-4">
                        <span className="text-[11px] bg-white/20 px-2.5 py-1 rounded font-bold">Use: {offer.code}</span>
                      </div>
                      <button className="mt-4 text-xs font-bold underline underline-offset-2 group-hover:no-underline">BOOK NOW</button>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Why Nikaba */}
            <section className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Why Book with Nikaba Travels?</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: Shield, title: 'Trusted Partners', desc: 'Skyscanner, RedBus & IRCTC verified' },
                  { icon: Percent, title: 'Best Prices', desc: 'Compare across top platforms instantly' },
                  { icon: Sparkles, title: 'All-in-One Search', desc: 'Flights, buses & trains in one place' },
                  { icon: Gift, title: 'Exclusive Deals', desc: 'Members-only offers & rewards' },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-[#243185]/5 flex items-center justify-center mx-auto mb-3">
                      <item.icon size={22} className="text-[#243185]" />
                    </div>
                    <p className="text-sm font-bold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Handpicked Collections */}
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900">Handpicked Collections for You</h2>
                <button className="text-sm text-[#243185] font-semibold hover:underline flex items-center gap-1">
                  View All <ChevronRight size={14} />
                </button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x">
                {COLLECTIONS.map((c, i) => (
                  <div key={i} className="flex-shrink-0 w-52 snap-start group cursor-pointer">
                    <div className="relative rounded-xl overflow-hidden h-60">
                      <img src={c.img} alt={c.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <span className="absolute top-3 left-3 text-[10px] bg-white text-[#243185] px-2 py-1 rounded font-black">{c.tag}</span>
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white text-sm font-bold">{c.title}</p>
                        <p className="text-white/70 text-xs mt-0.5">Starting from <span className="text-white font-bold">{c.from}</span></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Trusted Partners */}
            <section className="bg-white rounded-2xl p-8 shadow-sm text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Trusted by Thousands of Travellers</h2>
              <p className="text-sm text-gray-500 mb-8">We partner with India's best travel platforms</p>
              <div className="flex items-center justify-center gap-8 flex-wrap opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
                {['Skyscanner', 'RedBus', 'IRCTC', 'IndiGo', 'Air India', 'SpiceJet', 'VRL Travels'].map(name => (
                  <div key={name} className="flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-lg">
                    <Plane size={16} className="text-gray-400" />
                    <span className="text-sm font-semibold text-gray-600">{name}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Search;
